/**
 * Read accessors for `createRecordStore.ts` — pure lookups over the store's
 * `records`/`snapshot` state (batch-aware via injected accessors), split out
 * to keep the primary file under the project's line-count guidance.
 */

import type { StoredRecord } from './recordStoreTypes';

export interface ReadEngineDeps<T> {
  getRecords: () => Map<string, StoredRecord<T>>;
  getSnapshot: () => ReadonlyMap<string, StoredRecord<T>>;
  getBatchDepth: () => number;
  getBatchRecords: () => Map<string, StoredRecord<T>> | null;
}

export interface ReadEngine<T> {
  getRecord(id: string): StoredRecord<T> | undefined;
  getData(id: string): T | undefined;
  getSeq(id: string): number | undefined;
  getSnapshot(): ReadonlyMap<string, StoredRecord<T>>;
  hasComplete(id: string): boolean;
  getCompleteIds(): string[];
  isStale(id: string, ttlMs: number): boolean;
}

export function createReadEngine<T>(deps: ReadEngineDeps<T>): ReadEngine<T> {
  function getRecord(id: string): StoredRecord<T> | undefined {
    const source =
      deps.getBatchDepth() > 0 ? deps.getBatchRecords() : deps.getRecords();
    if (!source) {
      throw new Error('Batch state inconsistency');
    }
    return source.get(id);
  }

  function getData(id: string): T | undefined {
    return getRecord(id)?.data;
  }

  function getSeq(id: string): number | undefined {
    return getRecord(id)?.meta.lastSeq;
  }

  function hasComplete(id: string): boolean {
    return deps.getRecords().get(id)?.meta.isComplete ?? false;
  }

  function getCompleteIds(): string[] {
    const ids: string[] = [];
    for (const [id, stored] of deps.getRecords()) {
      if (stored.meta.isComplete) {
        ids.push(id);
      }
    }
    return ids;
  }

  function isStale(id: string, ttlMs: number): boolean {
    const stored = deps.getRecords().get(id);
    if (!stored || stored.meta.fetchedAt === 0) {
      return true;
    }
    return Date.now() - stored.meta.fetchedAt > ttlMs;
  }

  return {
    getRecord,
    getData,
    getSeq,
    getSnapshot: deps.getSnapshot,
    hasComplete,
    getCompleteIds,
    isStale,
  };
}
